import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  costWeeksAPI, type CostTermsGroup, type CostWeek, type CostWeekLine, type CostWeekLinesResponse, type CostWeeksChart,
} from '../../../lib/api'
import { useFormatters } from '../../../contexts/PreferencesContext'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table'
import { Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { notifyExportResult, type ExportTruncation } from '../../../lib/exportTruncation'
import ExportTruncationBanner from '../../export/ExportTruncationBanner'
import { DrilldownPager } from '../po-lines/DrilldownPager'

const PAGE_SIZE = 50

/** Columns shown as dates, and as right-aligned figures (prices in the PO's currency). */
const DATES = new Set<keyof CostWeekLine>(['supplier_promise_date', 'customer_required_date', 'expected_payment_date', 'order_date'])
const NUMERIC = new Set<keyof CostWeekLine>(['quantity', 'unit_price', 'contract_price', 'ppv', 'total_value', 'outstanding_value'])
/** Name columns get room enough not to wrap a short company name onto four lines. */
const NAMES = new Set<keyof CostWeekLine>(['customer', 'supplier_group', 'supplier', 'part_description'])

const cellClass = (key: keyof CostWeekLine) =>
  key === 'po_number' ? 'font-medium whitespace-nowrap'
    : NUMERIC.has(key) ? 'text-right tabular-nums whitespace-nowrap'
    : DATES.has(key) ? 'whitespace-nowrap'
    : NAMES.has(key) ? 'min-w-[10rem]' : undefined

export interface CostWeekLinesTarget {
  chart: CostWeeksChart
  bar: CostWeek
  /** The terms tab to open on: a group key, or "all". */
  terms: string
  /** CR-1421 decision 4: list only the week's arrears lines (an arrears bar was clicked). */
  subset?: 'arrears'
}

interface CostWeekLinesModalProps {
  target: CostWeekLinesTarget | null
  groups: CostTermsGroup[]
  params: Record<string, string | number | boolean>
  onClose: () => void
}

const TITLES: Record<CostWeeksChart, string> = { incoming: 'Incoming', payments: 'Payments Due' }

/** The `terms` request value for a tab: its key, Other's members, or every group. */
function termsParam(key: string, groups: CostTermsGroup[]): string {
  if (key === 'all') return 'all'
  const g = groups.find((x) => x.key === key)
  return g?.members?.join(',') ?? key
}

/**
 * CR-1421 (Aaron 1c): one modal per bar of the Cost weekly charts, on the
 * `POLineDrilldownModal` pattern — tabs "All" and each terms group with line
 * counts, opened on the clicked section; count and value for the active tab;
 * Export to Excel; a numbered pager. Rows carry Payment Terms and Expected
 * Payment Date.
 */
export default function CostWeekLinesModal({ target, groups, params, onClose }: CostWeekLinesModalProps) {
  const { formatCurrency, formatDate } = useFormatters()
  const [active, setActive] = useState('all')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<CostWeekLinesResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [truncation, setTruncation] = useState<ExportTruncation | null>(null)

  useEffect(() => {
    if (target) { setActive(target.terms); setPage(1); setData(null); setTruncation(null) }
  }, [target])

  const bar = useMemo(() => target && ({
    chart: target.chart,
    week: target.bar.week_start ?? 'overdue',
    terms: termsParam(active, groups),
    ...(target.subset ? { subset: target.subset } : {}),
  }), [target, active, groups])

  useEffect(() => {
    if (!bar) return
    let live = true
    setLoading(true)
    setError(null)
    costWeeksAPI.getLines(params, bar, page, PAGE_SIZE)
      .then((r) => { if (live) setData(r.data) })
      .catch((err: unknown) => {
        console.error('Cost week lines failed:', err)
        if (live) setError('Could not load the lines for this bar. Close and click the bar again.')
      })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [bar, page, params])

  const handleExport = useCallback(async () => {
    if (!bar) return
    setExporting(true)
    try {
      const resp = await costWeeksAPI.exportLines(params, bar)
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `cost_${bar.chart}_${bar.week}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
      setTruncation(notifyExportResult(resp, 'Excel export ready'))
    } catch (err: unknown) {
      console.error('Cost week lines export failed:', err)
      toast.error('Failed to export Excel')
    } finally {
      setExporting(false)
    }
  }, [bar, params])

  if (!target) return null
  const renderCell = (key: keyof CostWeekLine, v: CostWeekLine[keyof CostWeekLine]) => {
    if (key === 'payment_terms') return v ?? <span className="text-muted-foreground">Not set</span>
    if (v === null || v === undefined || v === '') return '–'
    if (DATES.has(key)) return formatDate(String(v))
    if (key === 'outstanding_value') return formatCurrency(Number(v))
    if (key === 'quantity') return Number(v).toLocaleString()
    if (NUMERIC.has(key)) return Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    return String(v)
  }
  // An arrears bar has no terms split of its own, so its list is the one tab.
  const arrearsOnly = target.subset === 'arrears'
  const arrearsLines = (target.bar as { arrears_lines?: number }).arrears_lines ?? 0
  const tabs = arrearsOnly
    ? [{ key: 'all', label: 'All', count: arrearsLines }]
    : [
      { key: 'all', label: 'All', count: target.bar.lines },
      ...groups.filter((g) => target.bar.terms[g.key]).map((g) => ({ key: g.key, label: g.label, count: target.bar.terms[g.key].lines })),
    ]
  // The arrears list is every open line promised before this week, however old: not the week's
  // dates (CR-9124 Option B: a line promised earlier this week is still this week's incoming).
  const when = arrearsOnly
    ? 'Open lines promised before this week'
    : target.bar.week_start
    ? `${target.bar.label}, ${formatDate(target.bar.week_start)} – ${formatDate(target.bar.week_end)}`
    : `Payments due by ${formatDate(target.bar.week_end)}, before this week`
  const total = data?.count ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="!max-w-[95vw] w-[1800px]">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle>{arrearsOnly ? 'Arrears' : TITLES[target.chart]}, {target.bar.label}</DialogTitle>
              <DialogDescription>
                {when}{data ? ` • ${total.toLocaleString()} PO line${total === 1 ? '' : 's'} • ${formatCurrency(data.value)}` : ''}
              </DialogDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || total === 0}>
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Export to Excel
            </Button>
          </div>
          <ExportTruncationBanner truncation={truncation} onDismiss={() => setTruncation(null)} />
        </DialogHeader>

        <div className="flex flex-wrap gap-1 border-b pb-2" role="tablist" aria-label="Payment terms">
          {tabs.map((t) => (
            <button
              key={t.key} type="button" role="tab" aria-selected={t.key === active}
              onClick={() => { setActive(t.key); setPage(1) }}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                t.key === active ? 'bg-primary text-primary-foreground font-medium' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {t.label}<span className="ml-1 tabular-nums opacity-80">({t.count.toLocaleString()})</span>
            </button>
          ))}
        </div>

        {error && <div className="text-sm text-red-600 py-3">{error}</div>}
        {loading && !data ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
        ) : total === 0 && !loading && !error ? (
          <div className="text-sm text-muted-foreground py-10 text-center">No PO lines in this bar.</div>
        ) : data && (
          <div className="max-h-[60vh] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {data.columns.map((c) => (
                    <TableHead key={c.key} className={`whitespace-nowrap ${NUMERIC.has(c.key) ? 'text-right' : ''}`}>{c.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.results.map((r) => (
                  <TableRow key={r.id}>
                    {data.columns.map((c) => (
                      <TableCell key={c.key} className={cellClass(c.key)}>{renderCell(c.key, r[c.key])}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <DrilldownPager page={page} totalPages={totalPages} loading={loading} onPageChange={setPage} idPrefix="cwl" />
        )}
      </DialogContent>
    </Dialog>
  )
}
